import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { currentLocale } from '../../i18n';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import './WalletChip.css';

/**
 * WalletChip — poängsaldot (tipsplånboken) i headern.
 *
 * Visar det spenderbara saldot som en valuta-chip bredvid notisklockan och
 * öppnar en dropdown med de senaste transaktionerna (intjänade tips,
 * startkapital, framtida köp/insatser). Saldot är personligt: chipen renderas
 * bara för inloggade användare och API-anropet går med JWT — 401 döljer
 * chipen tyst i stället för att visa ett fel.
 *
 * @returns {JSX.Element|null} Chipen med dropdown, eller null utloggad.
 */
export default function WalletChip() {
  const { t } = useTranslation();
  const { user, getAuthHeaders } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const loadWallet = useCallback(() => {
    fetch(`${API_URL}/api/predictions/wallet`, { headers: getAuthHeaders() })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => { if (body) setWallet(body); })
      .catch(() => {
        // tyst — plånboken är icke-kritisk för sidan
      });
  }, [getAuthHeaders]);

  useEffect(() => {
    if (user) loadWallet();
  }, [user, loadWallet]);

  // Butiken (m.fl.) dispatchar 'wallet-refresh' efter köp så saldot i
  // headern uppdateras direkt utan sidladdning.
  useEffect(() => {
    const handler = () => loadWallet();
    window.addEventListener('wallet-refresh', handler);
    return () => window.removeEventListener('wallet-refresh', handler);
  }, [loadWallet]);

  // Close on click outside
  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  if (!user || wallet === null) return null;

  const handleToggle = () => {
    // Färskt saldo vid öppning — tips kan ha rättats sedan sidladdningen.
    if (!open) loadWallet();
    setOpen((prev) => !prev);
  };

  const kindLabel = (kind) => {
    const key = {
      prediction: 'wallet.kindPrediction',
      welcome_bonus: 'wallet.kindWelcomeBonus',
      shop_purchase: 'wallet.kindShopPurchase',
      raffle_ticket: 'wallet.kindRaffleTicket',
      stake: 'wallet.kindStake',
      payout: 'wallet.kindPayout',
      refund: 'wallet.kindRefund',
      admin_adjust: 'wallet.kindAdminAdjust',
    }[kind];
    return key ? t(key) : kind;
  };

  return (
    <div className="wallet-chip" ref={ref}>
      <button
        type="button"
        className="wallet-chip-btn"
        onClick={handleToggle}
        title={t('wallet.title')}
      >
        <span className="wallet-chip-coin">🪙</span>
        <span className="wallet-chip-balance">{wallet.balance.toLocaleString(currentLocale())}</span>
      </button>

      {open && (
        <div className="wallet-dropdown">
          <div className="wallet-dropdown-header">
            <span className="wallet-dropdown-title">{t('wallet.title')}</span>
            <span className="wallet-dropdown-balance">
              🪙 {wallet.balance.toLocaleString(currentLocale())} {t('predictions.points')}
            </span>
          </div>
          <div className="wallet-dropdown-list">
            {(wallet.transactions || []).length === 0 ? (
              <div className="wallet-empty">{t('wallet.empty')}</div>
            ) : (
              wallet.transactions.slice(0, 10).map((txn, index) => (
                <div key={`${txn.created_at}-${index}`} className="wallet-txn">
                  <div className="wallet-txn-main">
                    <span className="wallet-txn-kind">{kindLabel(txn.kind)}</span>
                    {txn.note && <span className="wallet-txn-note">{txn.note}</span>}
                  </div>
                  <div className="wallet-txn-side">
                    <span className={`wallet-txn-amount ${txn.amount >= 0 ? 'positive' : 'negative'}`}>
                      {txn.amount >= 0 ? '+' : ''}{txn.amount}
                    </span>
                    <span className="wallet-txn-date">
                      {new Date(txn.created_at).toLocaleDateString(currentLocale())}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="wallet-dropdown-footer">{t('wallet.footerHint')}</div>
        </div>
      )}
    </div>
  );
}
